import { translate } from "app/filters/i18nFilter";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";
import { useEffect, useMemo, useState } from "react";
import { setSearchObjectCategories } from "app/pages/sales-intelligence/sub-modules/intent/store/management/actions/actions";
import { sortBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
    selectIntentCategories,
    selectIntentSearchObject,
} from "app/pages/sales-intelligence/sub-modules/intent/store/management/selectors/selectors";
import type { SearchIntentParams } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { fetchIntentTopicsCategoryThunk } from "app/pages/sales-intelligence/sub-modules/intent/store/management/effects/effects";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";

type CategoryDD = {
    id: string;
    children: string;
    showCheckBox: boolean;
    textIndent: boolean;
};

const IntentCategoryDropDown = () => {
    const [isSelectDutyValue, setIsSelectDutyValue] = useState<boolean>(false);

    const dispatch = useDispatch();

    const categories: readonly string[] = useSelector(selectIntentCategories);
    const searchObj: SearchIntentParams = useSelector(selectIntentSearchObject);

    const selectedCategories = searchObj.categories.reduce<Record<string, boolean>>((acc, elem: string) => {
        acc[elem] = true;
        return acc;
    }, {});

    useEffect(() => {
        void dispatch(fetchIntentTopicsCategoryThunk());
    }, []);

    const onSelectCategory = (category: CategoryDD): void => {
        const { categories } = searchObj;
        const isCategorySelected = typeof selectedCategories[category.id] !== "undefined";

        const updatedCategory = isCategorySelected
            ? categories.filter((cat) => cat !== category.children)
            : [...categories, category.children];

        if (!isCategorySelected) {
            void intentTrackingService.trackApplyCategoryAction(updatedCategory);
        }

        void dispatch(setSearchObjectCategories(updatedCategory));
    };
    const selectedCategoryText = useMemo(() => {
        const { categories } = searchObj;

        if (categories.length === 0) {return "";}

        const firstCategory = categories[0];
        const additionalCategoriesCount = categories.length - 1;

        return additionalCategoriesCount > 0 ? `${firstCategory} +${additionalCategoriesCount}` : firstCategory;
    }, [searchObj.categories]);
    const onClearCategory = () => {
        void dispatch(setSearchObjectCategories([]));
    };

    const categoriesOptions = useMemo(() => {
        if (categories) {
            const sortedCategories = sortBy(categories, (item) => {
                return selectedCategories[item];
            });

            return sortedCategories.map((item) => (
                <EllipsisDropdownItem
                    showCheckBox
                    id={item}
                    key={item}
                >
                    {item}
                </EllipsisDropdownItem>
            ));
        }
        return [];
    }, [categories, isSelectDutyValue]);

    const onSelectToggle = (isOpen: boolean) => {
        if (isOpen) {
            void intentTrackingService.trackCategoryFilterClickAction();
        }

        setIsSelectDutyValue(!isOpen);
    };

    return (
        <ChipDownContainer
            width={300}
            onClick={onSelectCategory}
            selectedIds={selectedCategories}
            selectedText={selectedCategoryText}
            onCloseItem={onClearCategory}
            onToggle={onSelectToggle}
            dropdownPopupPlacement={"ontop-right"}
            closeOnItemClick={false}
            tooltipText={searchObj.categories.join(", ")}
            buttonText={translate("si.intent.management.add-topic.category-dd.btn-text")}
            searchPlaceHolder={translate("si.intent.management.add-topic.category-dd.search-placeholder")}
            hasSearch={true}
            noResultsMessage={translate("dropdown.no.results")}
        >
            {categoriesOptions}
        </ChipDownContainer>
    );
};

export default IntentCategoryDropDown;
